import { useEffect, useRef, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// Maps a PTI Arcade game slug to its self-contained static game bundle.
const GAMES = {
  "pti-hextris": { title: "Ink Hex", src: "/games/pti-hextris/index.html", responsive: true },
  "pti-tower-defense": { title: "Ink Defense", src: "/games/pti-tower-defense/index.html", responsive: true },
  "pti-space-shooter": { title: "Galaxy Battle", src: "/games/pti-space-shooter/index.html", responsive: true },
};

// Logical canvas the games are laid out at; we scale this to fit with no scrollbars.
const LOGICAL_W = 1120;
const LOGICAL_H = 1080;

export default function GamePlayer() {
  const { slug } = useParams();
  const boxRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const fit = () => {
      const el = boxRef.current;
      if (!el) return;
      setScale(Math.min(el.clientWidth / LOGICAL_W, el.clientHeight / LOGICAL_H));
    };
    fit();
    window.addEventListener("resize", fit);
    const t = setTimeout(fit, 300);
    return () => { window.removeEventListener("resize", fit); clearTimeout(t); };
  }, [slug]);

  const game = GAMES[slug];
  if (!game) return <Navigate to="/pti-play" replace />;

  // Responsive games fill the frame directly (no fixed-canvas scaling).
  if (game.responsive) {
    return (
      <div className="bg-pti-black text-white h-[100vh] flex flex-col overflow-hidden" data-testid={`game-player-${slug}`}>
        <div className="pti-container pt-4 pb-2 shrink-0">
          <Link to="/pti-play" className="inline-flex items-center gap-1 text-sm text-neutral-400 hover:text-white"><ArrowLeft size={16} /> Back to Arcade</Link>
        </div>
        <div className="flex-1 min-h-0 w-full">
          <iframe src={game.src} title={game.title} data-testid="game-frame"
            style={{ width: "100%", height: "100%", border: 0, display: "block" }} allow="autoplay" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-pti-black text-white h-[100vh] flex flex-col overflow-hidden" data-testid={`game-player-${slug}`}>
      <div className="pti-container pt-4 pb-2 shrink-0">
        <Link to="/pti-play" className="inline-flex items-center gap-1 text-sm text-neutral-400 hover:text-white"><ArrowLeft size={16} /> Back to Arcade</Link>
      </div>
      <div ref={boxRef} className="flex-1 min-h-0 w-full flex justify-center items-start overflow-hidden px-2 pb-2">
        <iframe
          src={game.src}
          title={game.title}
          data-testid="game-frame"
          scrolling="no"
          style={{ width: LOGICAL_W, height: LOGICAL_H, transform: `scale(${scale})`, transformOrigin: "top center", border: 0, flex: "none" }}
          allow="autoplay"
        />
      </div>
    </div>
  );
}
